import { animate, style, transition, trigger } from "@angular/animations";
import { AfterViewInit, Component, ElementRef, Input, OnInit } from "@angular/core";
import { ChartService, ChartType, IHighchartsDataSeries } from "@services/charts.service";
import { ColourService } from "@services/colour.service";
import { ConstantsService } from "@services/constantsService";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import { IVeriTableBindings, IVeriTableCellGraphOptions, IVeriTableHeader, IVeriTableRow } from "../veriTable.component";
import "./veriTableContainer.scss";

declare const Highcharts: any;

export interface IVeriTableContainer {
    registerChild(veriTable: IVeriTableBindings<any>);
    onRowsUpdated();
    updateNumbers();
}

export interface IVeriTableChart {
    id: string;
    header: IVeriTableHeader;
    chartConfig: Highcharts.Options;
    chartObject?: Highcharts.ChartObject;
    title: string;
    seriesCount?: number;
}

export const animSlideIn = trigger('animSlideIn', [
    transition('void => *', [
        style({ height: '0px', opacity: '0' }),
        animate('.3s', style({ height: '*', opacity: '1' })),
    ])
]);

export const animSlideInOutFromLeft = trigger('animSlideInOutFromLeft', [
    transition(':enter', [
        style({ height: '0px', opacity: '0' }),
        animate('.3s', style({ height: '*', opacity: '1' })),
    ]),
    transition(':leave', [
        style({ height: '*', opacity: '0' }),
        animate('.3s', style({ height: '0', opacity: '0' })),
    ])
]);

@Component({
    selector: "veri-table-container",
    template: require("./veriTableContainer.html"),
    animations: [animSlideInOutFromLeft]
})
export class VeriTableContainerComponent implements OnInit, AfterViewInit, IVeriTableContainer {

    @Input() loading: boolean = false;
    @Input() maxEvents: number;

    childTable: IVeriTableBindings<any>;

    componentLoaded = false;

    isMobile: boolean = false;

    template = `hello2 <nav-count [count]="5"></nav-count>`;

    constructor(
        private readonly elementRef: ElementRef,
        private readonly chartService: ChartService,
        private readonly constantsService: ConstantsService,
        private readonly translationService: TranslationService,
        private readonly colourService: ColourService,
        private readonly veriDate: VeriDate) { }

    ngOnInit(): void {

        //setTimeout(() => { this.findChildTable(); }, 0);
        this.findChildTable();
        this.isMobile = this.constantsService.getIsMobile();
    }

    ngAfterViewInit() {

        this.componentLoaded = true;
    }

    registerChild(veriTable: IVeriTableBindings<any>) {
        this.childTable = veriTable;
        this.childTable.veriTableContainer = this;
        this.childTable.headers.forEach(h => {
            if (!h.graphOptions) {
                return;
            }

            h.graphOptions.toggleShowGraph = () => {
                h.graphOptions.show = !h.graphOptions.show;

                // Now update all others to reflow
                this.charts.forEach(chart => { this.reflowChart(chart); });

                if (h.graphOptions.show) {
                    this.createChartForHeader(h, this.childTable.displayRows);
                }
                else {

                    // Remove all charts that have been removed
                    const relatedChart = this.charts.find(c => c.header == h);
                    this.removeChart(relatedChart);
                }
            };
        });

        this.onRowsUpdated();
    }

    onRowsUpdated() {

        // Date chart test
        this.buildChart(this.childTable.displayRows);
    }

    updateNumbers() {
    }

    //------

    private findChildTable() {
        // Temp solution while veritable component still requires upgrading..
        const veriTable = this.elementRef.nativeElement.querySelector('veri-table');
        const element = window.angular.element(veriTable);
        const scope: any = element.length && (element.isolateScope() || element.scope());

        if (scope) {
            //console.log("findChildTable", element, veriTable);
            this.registerChild(scope.$ctrl);
        }
        else {
            console.error("Could not find veriTable in markup");
        }
    }





    // Date chart test
    charts: IVeriTableChart[] = [];

    removeChart(chart: IVeriTableChart) {
        if (!chart) {
            return;
        }

        const chartIndex = this.charts.indexOf(chart);
        const matchingHeader = this.childTable.headers.find(h => h == chart.header);

        if (matchingHeader) {
            matchingHeader.graphOptions.show = false;
        }

        chart.chartObject?.destroy();
        this.charts.splice(chartIndex, 1);

        this.charts.forEach(chart => {

            this.reflowChart(chart);
        });
    }

    private reflowChart(chart: IVeriTableChart) {
        setTimeout(() => {
            chart?.chartObject?.reflow();
        }, 0);

        setTimeout(() => {
            chart?.chartObject?.reflow();
        }, 300);
    }

    private buildChart(rows: IVeriTableRow[]) {
        this.charts = [];

        const graphHeaders = this.childTable.headers.filter(h => h.graphOptions?.show/* && h.sortType == SortType.Date*/);

        if (graphHeaders?.length == 0) {

            return;
        }

        this.childTable.graphOptionsAvailable = true;
        graphHeaders.forEach((header, index) => {
            this.createChartForHeader(header, rows);
        });
    }

    private createChartForHeader(header: IVeriTableHeader, rows: IVeriTableRow[]) {
        let veriTableChart = this.charts.find(c => c.header == header);

        if (!veriTableChart) {
            veriTableChart = {
                id: window.URL.createObjectURL(new Blob([])).split('/').pop(),
                header: header,
                title: header.graphOptions.title || header.title,
                chartConfig: null
            };

            if (header.graphOptions.maxSeries) {
                veriTableChart.title = this.translationService.t(`Common.TopX`, `${veriTableChart.title}s`); // ${header.graphOptions.maxSeries}
            }
            this.charts.push(veriTableChart);
        }

        // Timeout prevents drawing issues
        setTimeout(() => {
            veriTableChart.chartConfig = this.createChartDefinition(veriTableChart.header.graphOptions);
            const newSeries = this.getSeriesData(veriTableChart, rows);
            if (rows.length != 0) {
                if (veriTableChart.chartObject) {
                    veriTableChart.chartObject.series[0].update(newSeries[0], true);
                }
                else {
                    veriTableChart.chartConfig.series = newSeries;
                    veriTableChart.chartObject = Highcharts.chart(veriTableChart.id, veriTableChart.chartConfig);
                }
            }
            if (veriTableChart.chartConfig?.series?.length) {
                veriTableChart.seriesCount = veriTableChart.chartConfig.series[0].data.length;
            }
            else {
                veriTableChart.seriesCount = 0;
            }
            this.chartService.useVigetColours(veriTableChart.chartConfig, Math.max(newSeries.length));
        }, 300);
    }

    private createChartDefinition(graphOptions: IVeriTableCellGraphOptions): Highcharts.Options {

        const chartConfig = this.chartService.createChartOptions(graphOptions.chartType);
        chartConfig.chart.backgroundColor = "transparent";
        chartConfig.chart.plotBackgroundColor = "transparent";

        chartConfig.plotOptions.series = chartConfig.plotOptions.series || {
            point: {
                events: {}
            }
        };

        switch (graphOptions.chartType) {
            case "pie":
                this.createPieChartConfig(chartConfig);
                break;

            case "line":
            case "areaspline":
                chartConfig.plotOptions.areaspline.lineWidth = 1;
                break;
            case "column":
                if (graphOptions.stacked) {
                    chartConfig.plotOptions.column = {

                        stacking: 'normal'

                    }
                }
                break;
        }

        this.chartService.useVigetColours(chartConfig);
        return chartConfig;
    }


    private createPieChartConfig(chartConfig: Highcharts.Options) {


        chartConfig.plotOptions.pie.dataLabels.enabled = true;
        chartConfig.plotOptions.pie.dataLabels.style = {
            color: "#555", textOutline: "none"
        };
    }

    private getSeriesData(veriTableChart: IVeriTableChart, rows: IVeriTableRow[]): Highcharts.IndividualSeriesOptions[] {
        if (!veriTableChart.chartConfig.chart.type) {
            this.createChartDefinition(veriTableChart.header.graphOptions);
        }

        if (veriTableChart.header.graphOptions.chartType == "pie") {
            return this.convertDataToPieSeries(veriTableChart, rows);
        }
        else if (veriTableChart.header.graphOptions.chartType == "column" && veriTableChart.header.graphOptions.stacked) {
            if (!veriTableChart.header.graphOptions.dateColumn) {
                console.warn("No date column specified");
                return;
            }
            return this.getSeriesForStackedChart(veriTableChart, rows);
        }
        else {
            return this.convertDateHeaderToSeries(veriTableChart, rows);
        }
    }

    private convertDataToPieSeries(veriTableChart: IVeriTableChart, rows: IVeriTableRow[]): Highcharts.IndividualSeriesOptions[] {

        const dataSeries: Highcharts.IndividualSeriesOptions[] = [{
            name: veriTableChart.header.title,
            data: []
        }];

        const dataMap = new Map<string, Highcharts.DataPoint>();
        const columnIndex = this.getHeaderIndex(veriTableChart.header);

        rows.forEach(row => {
            const cell = row.cells[columnIndex];
            const cellTitle = cell.graphLabel || cell.summaryLabel || cell.contents;
            let matchingDataPoint = dataMap.get(cellTitle) as Highcharts.DataPoint;

            if (!matchingDataPoint) {
                matchingDataPoint = { name: cellTitle, y: 0 };
                dataMap.set(cellTitle, matchingDataPoint);
                dataSeries[0].data.push(matchingDataPoint);
            }

            matchingDataPoint.y++;
        });

        veriTableChart.seriesCount = dataSeries[0].data.length;

        if (veriTableChart.header.graphOptions.maxSeries) {
            dataSeries[0].data.sort((d1, d2) => {
                return (d1 as Highcharts.DataPoint).y < (d2 as Highcharts.DataPoint).y ? 1 : -1;
            });

            dataSeries[0].data.splice(veriTableChart.header.graphOptions.maxSeries);
        }

        dataSeries[0].data.forEach((d, i) => {
            const dp = d as Highcharts.DataPoint;
            dp.color = this.colourService.getVigetColor(i, dataSeries[0].data.length);
        });

        return dataSeries;
    }

    private getSeriesForStackedChart(veriTableChart: IVeriTableChart, rows: IVeriTableRow[]): IHighchartsDataSeries[] {
        const dataSeries: IHighchartsDataSeries[] = [];

        const dateColumn = veriTableChart.header.graphOptions.dateColumn;

        const columnIndex = this.getHeaderIndex(veriTableChart.header);

        const dataSet = [];

        rows.forEach(row => {
            if (!dataSet.includes(row.cells[columnIndex].sortData)) {
                dataSet.push(row.cells[columnIndex].sortData);
            }
        })

        for (let i = 0; i < dataSet.length; i++) {
            const datasetName = dataSet[i].charAt(0).toUpperCase() + dataSet[i].slice(1);
            const series = {
                name: datasetName,
                data: [],
                pointIntervalUnit: 'day',
                type: veriTableChart.header.graphOptions.chartType,
                color: this.colourService.getVigetColor(i, dataSet.length)
            };
            const data: [any, any][] = [];
            const dataByDay = this.countDataPerDayForStackedChart(rows, dateColumn, columnIndex, dataSet[i]);

            Object.keys(dataByDay).forEach((x, y) => {
                data.push([Number(x), dataByDay[x]]);
            });
            data.sort((x, y) => x[0] - y[0]);
            series.data = data;
            dataSeries.push(series);

        }


        return dataSeries;
    }


    private getHeaderIndex(header: IVeriTableHeader): number {
        return this.childTable.headers.indexOf(header);
    }

    private convertDateHeaderToSeries(veriTableChart: IVeriTableChart, rows: IVeriTableRow[]): IHighchartsDataSeries[] {

        const dataSeries: IHighchartsDataSeries[] = [];

        const series = {
            name: veriTableChart.title,
            data: [],
            pointIntervalUnit: 'day',
            type: veriTableChart.header.graphOptions.chartType
        };
        series.data = this.createSeriesData(veriTableChart.header, rows);
        dataSeries.push(series);
        return dataSeries;
    }

    private createSeriesData(header: IVeriTableHeader, rows: IVeriTableRow[]): [any, any][] {
        const data: [any, any][] = [];

        const columnIndex = this.getHeaderIndex(header);
        const dataByDay = this.countDataPerDay(rows, columnIndex);
        Object.keys(dataByDay).forEach((x, y) => {

            data.push([Number(x), dataByDay[x]]);
        });

        data.sort((x, y) => x[0] - y[0]);
        return data;
    }

    private countDataPerDay(rows: IVeriTableRow[], columnIndex: number): number[] {


        return rows.reduce((result, row) => {

            const dateAsMoment = row.cells[columnIndex].sortData > 0 ? this.veriDate.createMoment(row.cells[columnIndex].sortData) : null;
            const day = dateAsMoment ? dateAsMoment.startOf("day").valueOf() as number : null;

            if (day) {
                if (!result[day]) {
                    result[day] = 0;
                }
                result[day]++;
            }

            return result;
        }, []);
    }


    private countDataPerDayForStackedChart(rows: IVeriTableRow[], dateColumn: number, columnIndex: number, dataLabel: string) {
        return rows.reduce((result, row) => {

            const dateAsMoment = row.cells[dateColumn].sortData > 0 ? this.veriDate.createMoment(row.cells[dateColumn].sortData) : null;
            const day = dateAsMoment ? dateAsMoment.startOf("day").valueOf() as number : null;
            if (dataLabel === row.cells[columnIndex].sortData) {

                if (day) {
                    if (!result[day]) {
                        result[day] = 0;
                    }
                    result[day]++;
                }

                return result;

            }
            return result;
        }, []);
    }
}
